import React from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";

import { HeaderTitle } from "../../../../components/HeaderTitle";

import { IFormCreateAnimal } from "../../interface";
import { useNotification } from "../../../../hooks/useNotification";
import { createAnimal } from "../../../../services/animals";
import { useAuth } from "../../../../hooks/useAuth";
import { useTranslate } from "../../../../hooks/useTranslate";
import { formatDateToAPI } from "../../../../utils/format-date";
import { Paper, Stack } from "@mui/material";
import { FormContainer } from "react-hook-form-mui";
import AnimalForm from "../../components/animal-form";
import { Aptitude, Gender } from "../../enum";
import { isValid } from "date-fns";

export interface OptionSelect {
  label: string;
  id: string;
}

const CreateAnimal = () => {
  const { errorNotification, successNotification } = useNotification();
  const navigate = useNavigate();
  const { translate, currentLanguage } = useTranslate();

  const { propertiesIds } = useAuth();

  const schema = yup.object().shape({
    name: yup.string().required(translate("animals.name_required")),
    identifier: yup.string().required(translate("animals.identifier_required")),
    dob: yup
      .date()
      .nullable()
      .transform((v) => (isValid(v) ? v : null)),
    // .required(translate("global.dob_required")),
    type: yup.string().required(translate("global.type_required")),
    batch: yup.string(),
    flock: yup.string(),
    description: yup.string(),
    internship: yup
      .string()
      .nullable()
      .default(null)
      .required(translate("animals.internship_required")),
    // .when("gender", {
    //   is: (gender: Gender) => gender !== Gender.MALE,
    //   then: (scm) => scm.required(translate("animals.internship_required")),
    // }),
    race: yup.string().nullable(),
    // .required(translate("animals.race_required")),
    situation: yup
      .string()
      .nullable()
      .when(["gender", "type"], {
        is: (gender: Gender, type: Aptitude) =>
          gender === Gender.FEMALE && type === Aptitude.CUT,
        then: (scm) => scm.required(translate("animals.situation_required")),
      }),
    lactation: yup
      .string()
      .nullable()
      .when(["gender", "type"], {
        is: (gender: Gender, type: Aptitude) =>
          gender === Gender.FEMALE && type === Aptitude.CUT,
        then: (scm) => scm.required(translate("animals.lactation_required")),
      }),
    date_weighing: yup
      .date()
      .nullable()
      .transform((v) => (isValid(v) ? v : null)),
    // .required(translate("animals.date_weighing_required")),
    weighing: yup.string(),
    // .required(translate("animals.weighing_required")),
  });

  const form = useForm<IFormCreateAnimal>({
    resolver: yupResolver(schema),
    defaultValues: {
      gender: Gender.MALE,
    },
  });

  const values = form.watch();

  const submitForm = async (data: IFormCreateAnimal) => {
    // if (!image) return errorNotification(translate('animals.image_required'));

    const {
      name,
      dob,
      identifier,
      internship,
      race,
      weighing,
      date_weighing,
      batch,
      description,
      flock,
      breeding_animal,
      situation,
      lactation,
    } = data;

    try {
      const formData = new FormData();

      if (batch) formData.append("batch", batch ?? null);
      if (flock) formData.append("flock", flock ?? null);

      if (data.gender === Gender.FEMALE) {
        formData.append("breeding_animal", "false");
      } else {
        formData.append("breeding_animal", breeding_animal ? "true" : "false");
      }

      formData.append("name", name);

      formData.append("farm_id", propertiesIds[0]);
      formData.append("number_identifier", identifier);

      formData.append("gender", data.gender);
      formData.append("internship", internship ?? null);

      if (dob) {
        formData.append("date_birth", formatDateToAPI(new Date(dob)));
      }

      if (race) {
        formData.append("race", race);
      }

      if (date_weighing) {
        formData.append(
          "date_weighing",
          formatDateToAPI(new Date(date_weighing))
        );
      }

      if (weighing) {
        formData.append("weighing", String(weighing));
        formData.append(
          "typeWeighing",
          currentLanguage.language === "EN" ? "pounds" : "kg"
        );
      }

      formData.append("type", data.type);

      if (situation) {
        formData.append("situation", situation);
      }

      if (lactation) {
        formData.append("lactation", lactation ?? null);
      }

      formData.append("description", description ?? "");

      if (data.photo) {
        formData.append("photo", data.photo);
      }

      console.log(situation, race, lactation);

      await createAnimal(formData);
      // setLoading(false);
      successNotification(translate("animals.success_created_animal"));
      navigate("/animals");
    } catch (error) {
      console.error(error);

      errorNotification(translate("animals.error_created_animal"));
      // setLoading(false);
    }
  };

  return (
    <Stack spacing={4}>
      <HeaderTitle
        title={translate("animals.title_create")}
        url="/animals"
        showIcon
      />
      <Paper sx={{ p: 3, position: "relative", mt: values.photo ? -3 : 0 }}>
        <FormContainer formContext={form} onSuccess={submitForm}>
          <AnimalForm isNew={true} property={propertiesIds[0]} />
        </FormContainer>
      </Paper>
    </Stack>
  );
};

export { CreateAnimal };
